import { useState, useEffect } from "react";
import { usersApi, settingsApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  UserPlus,
  Edit2,
  Trash2,
  Shield,
  UserCheck,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const DEFAULT_ROLES = [
  { value: "salesperson", label: "Agent" },
  { value: "leader", label: "Leader" },
  { value: "admin", label: "Admin" },
];

const DEPARTMENTS = ["Sales", "CS"];
const COUNTRIES = ["India", "US"];

const TeamPage = () => {
  const { register, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [customRoles, setCustomRoles] = useState([]);
  const [newRoleName, setNewRoleName] = useState("");
  const [showRoleMaker, setShowRoleMaker] = useState(false);

  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    name: "",
    role: "salesperson",
    department: "Sales",
    country: ["India"],
  });

  useEffect(() => {
    fetchUsers();
    fetchCustomRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await usersApi.getAll();
      setUsers(response.data.users);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomRoles = async () => {
    try {
      const res = await settingsApi.getCustomRoles();
      setCustomRoles(res.data.roles || []);
    } catch (e) { console.debug("team: non-blocking custom-roles fetch failed", e); }
  };

  const allRoles = [...DEFAULT_ROLES, ...customRoles.map(r => ({ value: r.toLowerCase().replace(/\s+/g, "_"), label: r }))];

  const addCustomRole = async () => {
    const name = newRoleName.trim();
    if (!name) return;
    const exists = allRoles.some(r => r.label.toLowerCase() === name.toLowerCase());
    if (exists) { toast.error("Role already exists"); return; }
    try {
      await settingsApi.addCustomRole(name);
      setCustomRoles(prev => [...prev, name]);
      setNewRoleName("");
      toast.success(`Role "${name}" created`);
    } catch (err) { toast.error("Failed to create role"); }
  };

  const deleteCustomRole = async (roleName) => {
    try {
      await settingsApi.deleteCustomRole(roleName);
      setCustomRoles(prev => prev.filter(r => r !== roleName));
      toast.success(`Role "${roleName}" deleted`);
    } catch (err) { toast.error("Failed to delete role"); }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await register(newUser.email, newUser.password, newUser.name, newUser.role);
      toast.success("User created successfully");
      setShowAddUser(false);
      setNewUser({ email: "", password: "", name: "", role: "salesperson", department: "Sales", country: "India" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await usersApi.update(userId, updates);
      toast.success("User updated successfully");
      fetchUsers();
      setEditingUser(null);
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to deactivate this user?")) return;
    try {
      await usersApi.delete(userId);
      toast.success("User deactivated");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to deactivate user");
    }
  };

  const handlePromoteSuperAdmin = async (u) => {
    if (!window.confirm(`Promote ${u.name || u.email} to Super Admin? They will get full access to everything, including editing this permissions matrix.`)) return;
    try {
      await usersApi.promoteSuperAdmin(u.id);
      toast.success(`${u.name || u.email} is now Super Admin ⭐`);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Promote failed");
    }
  };

  const handleDemoteSuperAdmin = async (u) => {
    if (!window.confirm(`Demote ${u.name || u.email} back to Admin?`)) return;
    try {
      await usersApi.demoteToAdmin(u.id);
      toast.success(`${u.name || u.email} demoted to Admin`);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Demote failed");
    }
  };

  const activeUsers = users.filter((u) => u.is_active);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1
            className="text-3xl font-medium text-[#1C1917]"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            Team Management
          </h1>
          <p className="text-[#57534E] mt-1">
            {activeUsers.length} active members
          </p>
        </div>
        <button
          onClick={() => setShowAddUser(true)}
          className="btn-primary flex items-center gap-2"
          data-testid="add-user-btn"
        >
          <UserPlus size={18} /> Add Team Member
        </button>
      </div>

      {/* Role Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
        {allRoles.map((role) => {
          const count = activeUsers.filter((u) => u.role === role.value).length;
          return (
            <div key={role.value} className="stats-card" data-testid={`role-count-${role.value}`}>
              <div className="flex items-center gap-3">
                <div
                  className={`p-2 rounded-md ${
                    role.value === "admin"
                      ? "bg-[#BC4B35]/10 text-[#BC4B35]"
                      : role.value === "leader"
                      ? "bg-[#312E81]/10 text-[#312E81]"
                      : "bg-[#15803D]/10 text-[#15803D]"
                  }`}
                >
                  {role.value === "admin" ? <Shield size={20} /> : role.value === "leader" ? <Shield size={20} /> : <UserCheck size={20} />}
                </div>
                <div>
                  <p className="stats-label">{role.label}s</p>
                  <p className="stats-value">{count}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Role Maker */}
      <div className="mb-6">
        <button onClick={() => setShowRoleMaker(!showRoleMaker)}
          className="flex items-center gap-2 text-sm font-bold text-[--accent] hover:underline mb-2"
          data-testid="toggle-role-maker">
          <Shield size={14} /> {showRoleMaker ? "Hide" : "Manage"} Custom Roles
        </button>
        {showRoleMaker && (
          <div className="card p-4 space-y-3 animate-scale-in" data-testid="role-maker">
            <div className="flex gap-2">
              <input type="text" value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="New role name (e.g., Supervisor, QA Lead)"
                onKeyDown={(e) => { if (e.key === "Enter") addCustomRole(); }}
                className="form-input flex-1" data-testid="new-role-input" />
              <button onClick={addCustomRole} disabled={!newRoleName.trim()} className="btn-primary text-sm" data-testid="add-role-btn">Add Role</button>
            </div>
            {customRoles.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {customRoles.map(r => (
                  <span key={r} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-xl text-xs font-bold border border-purple-200" data-testid={`custom-role-${r}`}>
                    {r}
                    <button onClick={() => deleteCustomRole(r)} className="text-purple-400 hover:text-red-500 transition-colors" data-testid={`delete-role-${r}`}>&times;</button>
                  </span>
                ))}
              </div>
            )}
            {customRoles.length === 0 && <p className="text-xs text-[--text-muted]">No custom roles yet. Default roles: Agent, Leader, Admin</p>}
          </div>
        )}
      </div>

      {/* Page-Level Permission Matrix (super_admin only) */}
      <PermissionsMatrix />

      {/* Active Users */}
      <div className="bento-card mb-6">
        <h2
          className="text-xl font-medium text-[#1C1917] mb-4"
          style={{ fontFamily: "Playfair Display, serif" }}
        >
          Active Team Members
        </h2>

        {activeUsers.length === 0 ? (
          <div className="empty-state py-8">
            <p>No active team members</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Department</th>
                  <th>Country</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeUsers.map((user) => (
                  <tr key={user.id} data-testid={`user-row-${user.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="avatar">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-[#1C1917]">
                            {user.name}
                          </p>
                          <p className="text-sm text-[#57534E]">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`px-2 py-1 text-xs uppercase tracking-wider rounded-full font-bold ${
                          user.role === "super_admin"
                            ? "bg-amber-100 text-amber-800 border border-amber-300"
                            : user.role === "admin"
                            ? "bg-red-50 text-red-700"
                            : user.role === "leader"
                            ? "bg-purple-50 text-purple-700"
                            : "bg-green-50 text-green-700"
                        }`}
                      >
                        {user.role === "super_admin" ? "⭐ SUPER" : user.role}
                      </span>
                    </td>
                    <td>
                      <span className={`px-2 py-1 text-xs rounded-full font-bold ${user.department === "CS" ? "bg-blue-50 text-blue-700" : "bg-orange-50 text-orange-700"}`}>
                        {user.department || "Sales"}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-1">
                        {(Array.isArray(user.country) ? user.country : [user.country || "India"]).map(c => (
                          <span key={c} className="text-xs font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{c === "India" ? "🇮🇳 India" : "🇺🇸 US"}</span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className="text-sm text-[#57534E]">
                        {new Date(user.created_at).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEditingUser(user)}
                          className="btn-ghost p-2"
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Edit2 size={16} />
                        </button>
                        {currentUser?.role === "super_admin" && user.role !== "super_admin" && (
                          <button
                            onClick={() => handlePromoteSuperAdmin(user)}
                            className="btn-ghost p-2 text-amber-600 hover:text-amber-800"
                            title="Promote to Super Admin"
                            data-testid={`promote-${user.id}`}
                          >
                            ⭐
                          </button>
                        )}
                        {currentUser?.role === "super_admin" && user.role === "super_admin" && user.id !== currentUser?.id && (
                          <button
                            onClick={() => handleDemoteSuperAdmin(user)}
                            className="btn-ghost p-2 text-gray-500 hover:text-gray-800"
                            title="Demote to Admin"
                            data-testid={`demote-${user.id}`}
                          >
                            ↓
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="btn-ghost p-2 text-[#BE123C] hover:text-[#BE123C]"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Inactive Users */}
      {/* Add User Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="bg-[#FDFBF7] border-[#E7E5E4]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Playfair Display, serif" }}>
              Add Team Member
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-[#1C1917] mb-1">
                Full Name *
              </label>
              <input
                type="text"
                value={newUser.name}
                onChange={(e) =>
                  setNewUser((prev) => ({ ...prev, name: e.target.value }))
                }
                className="form-input"
                required
                data-testid="new-user-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1C1917] mb-1">
                Email *
              </label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) =>
                  setNewUser((prev) => ({ ...prev, email: e.target.value }))
                }
                className="form-input"
                required
                data-testid="new-user-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1C1917] mb-1">
                Password *
              </label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) =>
                  setNewUser((prev) => ({ ...prev, password: e.target.value }))
                }
                className="form-input"
                required
                minLength={6}
                data-testid="new-user-password"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1C1917] mb-1">
                Role *
              </label>
              <div className="flex gap-1.5 flex-wrap" data-testid="new-user-role">
                {allRoles.map((role) => (
                  <button key={role.value} type="button" onClick={() => setNewUser(prev => ({ ...prev, role: role.value }))}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold border-2 transition-all ${newUser.role === role.value ? "bg-[--accent] text-white border-[--accent] shadow-sm" : "bg-white text-[--text-secondary] border-gray-200 hover:border-[--accent-soft]"}`}
                    data-testid={`new-user-role-${role.value}`}>
                    {role.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-[#1C1917] mb-1">Department *</label>
                <div className="flex gap-1.5" data-testid="new-user-department">
                  {DEPARTMENTS.map((d) => (
                    <button key={d} type="button" onClick={() => setNewUser(prev => ({ ...prev, department: d }))}
                      className={`flex-1 py-2 rounded-xl text-sm font-bold border-2 transition-all ${newUser.department === d ? "bg-[--accent] text-white border-[--accent] shadow-sm" : "bg-white text-[--text-secondary] border-gray-200 hover:border-[--accent-soft]"}`}
                      data-testid={`new-user-dept-${d}`}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1C1917] mb-1">Country *</label>
                <div className="flex gap-2" data-testid="new-user-country">
                  {COUNTRIES.map((c) => {
                    const countries = Array.isArray(newUser.country) ? newUser.country : [newUser.country || "India"];
                    const active = countries.includes(c);
                    return (
                      <button key={c} type="button" onClick={() => {
                        const curr = Array.isArray(newUser.country) ? newUser.country : [newUser.country || "India"];
                        const updated = active ? curr.filter(x => x !== c) : [...curr, c];
                        if (updated.length > 0) setNewUser(prev => ({ ...prev, country: updated }));
                      }}
                        className={`flex-1 py-2 rounded-xl text-sm font-bold border-2 transition-all flex items-center justify-center gap-1.5 ${active ? "bg-[--accent] text-white border-[--accent] shadow-sm" : "bg-white text-[--text-secondary] border-gray-200 hover:border-[--accent-soft]"}`}
                        data-testid={`new-user-country-${c}`}>
                        {c === "India" ? "🇮🇳" : "🇺🇸"} {c}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <button
                type="button"
                onClick={() => setShowAddUser(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                data-testid="submit-new-user"
              >
                Add User
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="bg-[#FDFBF7] border-[#E7E5E4]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Playfair Display, serif" }}>
              Edit User
            </DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-[#1C1917] mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={editingUser.name}
                  onChange={(e) =>
                    setEditingUser((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="form-input"
                  data-testid="edit-user-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1C1917] mb-1">
                  Role
                </label>
                <div className="flex gap-1.5 flex-wrap" data-testid="edit-user-role">
                  {allRoles.map((role) => (
                    <button key={role.value} type="button" onClick={() => setEditingUser(prev => ({ ...prev, role: role.value }))}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold border-2 transition-all ${editingUser.role === role.value ? "bg-[--accent] text-white border-[--accent] shadow-sm" : "bg-white text-[--text-secondary] border-gray-200 hover:border-[--accent-soft]"}`}
                      data-testid={`edit-user-role-${role.value}`}>
                      {role.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[#1C1917] mb-1">Department</label>
                  <div className="flex gap-1.5" data-testid="edit-user-department">
                    {DEPARTMENTS.map((d) => (
                      <button key={d} type="button" onClick={() => setEditingUser(prev => ({ ...prev, department: d }))}
                        className={`flex-1 py-2 rounded-xl text-sm font-bold border-2 transition-all ${(editingUser.department || "Sales") === d ? "bg-[--accent] text-white border-[--accent] shadow-sm" : "bg-white text-[--text-secondary] border-gray-200 hover:border-[--accent-soft]"}`}
                        data-testid={`edit-user-dept-${d}`}>
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1C1917] mb-1">Country</label>
                  <div className="flex gap-2" data-testid="edit-user-country">
                    {COUNTRIES.map((c) => {
                      const countries = Array.isArray(editingUser.country) ? editingUser.country : [editingUser.country || "India"];
                      const active = countries.includes(c);
                      return (
                        <button key={c} type="button" onClick={() => {
                          const curr = Array.isArray(editingUser.country) ? editingUser.country : [editingUser.country || "India"];
                          const updated = active ? curr.filter(x => x !== c) : [...curr, c];
                          if (updated.length > 0) setEditingUser(prev => ({ ...prev, country: updated }));
                        }}
                          className={`flex-1 py-2 rounded-xl text-sm font-bold border-2 transition-all flex items-center justify-center gap-1.5 ${active ? "bg-[--accent] text-white border-[--accent] shadow-sm" : "bg-white text-[--text-secondary] border-gray-200 hover:border-[--accent-soft]"}`}
                          data-testid={`edit-user-country-${c}`}>
                          {c === "India" ? "🇮🇳" : "🇺🇸"} {c}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button
                  onClick={() => setEditingUser(null)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    handleUpdateUser(editingUser.id, {
                      name: editingUser.name,
                      role: editingUser.role,
                      department: editingUser.department || "Sales",
                      country: Array.isArray(editingUser.country) ? editingUser.country : [editingUser.country || "India"],
                    })
                  }
                  className="btn-primary"
                  data-testid="save-user-changes"
                >
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamPage;

/* ---- Page-Level Permission Matrix (super_admin edits; others view) ---- */
const PermissionsMatrix = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const isSuperAdmin = user?.role === "super_admin";

  const load = async () => {
    try {
      const r = await settingsApi.getPagePermissions();
      setData(r.data);
      setDraft(JSON.parse(JSON.stringify(r.data.permissions || {})));
    } catch (e) { toast.error("Load failed"); }
  };

  useEffect(() => { load(); }, []);

  const toggle = (role, page) => {
    if (role === "super_admin") return; // non-editable
    setDraft((prev) => {
      const next = { ...prev };
      const cur = new Set(next[role] || []);
      if (cur.has(page)) cur.delete(page); else cur.add(page);
      next[role] = [...cur];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...draft };
      delete payload.super_admin; // not editable server-side either
      await settingsApi.updatePagePermissions(payload);
      toast.success("Permissions saved. Users will see changes on next page load.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!data) return null;
  const pages = data.all_pages || [];
  const roles = Object.keys(draft || {}).sort((a, b) => {
    const order = { super_admin: 0, admin: 1, leader: 2, salesperson: 3 };
    return (order[a] ?? 99) - (order[b] ?? 99);
  });
  const changed = JSON.stringify(draft) !== JSON.stringify(data.permissions);

  return (
    <div className="mb-6">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-sm font-bold text-[--accent] hover:underline mb-2" data-testid="toggle-permissions-matrix">
        🔐 {open ? "Hide" : "Manage"} Page Permissions
        {!isSuperAdmin && <span className="text-[10px] text-[--text-muted] font-normal">(view-only — super admin required to edit)</span>}
      </button>
      {open && (
        <div className="card p-4 animate-scale-in" data-testid="permissions-matrix">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="permissions-table">
              <thead>
                <tr className="border-b border-[--border]">
                  <th className="text-left py-2 px-2 font-bold text-[--text-muted] uppercase text-[10px]">Page</th>
                  {roles.map((r) => (
                    <th key={r} className="text-center py-2 px-2 font-bold text-[--text-muted] uppercase text-[10px]">
                      {r === "super_admin" ? "Super Admin" : r[0].toUpperCase() + r.slice(1)}
                      {r === "super_admin" && <div className="text-[8px] text-amber-600 font-normal">locked</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pages.map((page) => (
                  <tr key={page} className="border-b border-[--border]/50 hover:bg-gray-50">
                    <td className="py-2 px-2 font-semibold text-[--text-primary]">{page}</td>
                    {roles.map((role) => {
                      const checked = (draft[role] || []).includes(page);
                      const locked = role === "super_admin" || !isSuperAdmin;
                      return (
                        <td key={role + page} className="text-center py-2 px-2">
                          <input type="checkbox" checked={checked}
                            onChange={() => toggle(role, page)}
                            disabled={locked}
                            className="w-4 h-4 accent-[--accent] cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                            data-testid={`perm-${role}-${page}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {isSuperAdmin && (
            <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-[--border]">
              <button onClick={() => setDraft(JSON.parse(JSON.stringify(data.permissions || {})))} disabled={!changed || saving} className="btn-secondary text-xs disabled:opacity-40">Reset</button>
              <button onClick={save} disabled={!changed || saving} className="btn-primary text-xs disabled:opacity-40" data-testid="save-permissions-btn">
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
